@if (isMobile$ | async) {
  <fcom-filters
    [isMobile$]="isMobile$"
    [isModalOpen]="isModalOpen"
    [hasFiltering]="(activeFilters$ | async) > 0"
    [buttonText]="'flightFilters.title' | finLocalization"
    [notificationContent]="'flightFilters.modalNotification' | finLocalization"
    [numberToShowWhenFiltered]="activeFilters$ | async"
    [displayNotification]="(numberOfFilteredDestinations$ | async) === 0"
    (showModal)="showModal()"
    (closeModal)="closeModal()"
    [modalTitle]="'destinationSearch.filters.title' | finLocalization"
    (primaryFooterButtonClick)="closeModal()"
    [primaryFooterButtonTitle]="
      'destinationSearch.filters.showResults' | finLocalization: { results: (numberOfFilteredDestinations$ | async) }
    "
    [secondaryFooterButtonTitle]="'destinationSearch.filters.clear' | finLocalization"
    (secondaryFooterButtonClick)="resetAllFilters()"
    [attr.data-testid]="'destination-search-filters-mobile'"
  >
    <ng-container modal-content>
      <div class="flex flex--column grow pr-large-x">
        <div class="grow">
          @if ((selectedDestinations$ | async)?.length > 0) {
            <fcom-notification role="status" [compact]="true" class="ps-small-t">
              <div class="flex-4">
                <div class="font-body-1 regular-type ps-small-l">
                  <span class="ps-xsmall-r ordic-blue-900-text">
                    @if ((selectedDestinations$ | async)?.length < 3) {
                      {{
                        'destinationSearch.selectedDestination.' +
                          ((selectedDestinations$ | async).length === 1 ? 'singular' : 'plural')
                          | finLocalization: { total: (selectedDestinations$ | async).length }
                      }}:
                      <span class="bold-type">{{ (selectedDestinations$ | async).join(', ') }}</span>
                    } @else {
                      {{
                        'destinationSearch.selectedDestination.' +
                          ((selectedDestinations$ | async).length === 1 ? 'singular' : 'plural')
                          | finLocalization: { total: (selectedDestinations$ | async).length }
                      }}
                    }
                  </span>
                </div>
              </div>
            </fcom-notification>
          }
          @if (showStopFilters) {
            <h4 class="nordic-blue-900-text font-heading-5 ps-medium-y">
              {{ 'destinationSearch.filters.stopsLabel' | finLocalization }}
            </h4>
            <ng-container *ngTemplateOutlet="stopsFilter"></ng-container>
            <div class="border-bottom"></div>
          }
          <h4 class="nordic-blue-900-text font-heading-5 ps-medium-y" style="margin-top: 0">
            {{ 'destinationSearch.filters.priceLabel' | finLocalization }}
          </h4>
          <ng-container *ngTemplateOutlet="priceFilter"></ng-container>
        </div>
      </div>
    </ng-container>
  </fcom-filters>
} @else {
  <div class="filter-container flex column-gap flex--middle">
    <fcom-filters
      [hasFiltering]="priceFilteringActive$ | async"
      [headerText]="'destinationSearch.filters.priceLabel' | finLocalization"
      [buttonText]="'destinationSearch.filters.priceLabel' | finLocalization"
      [buttonTextWhenFiltered]="
        priceButtonLabelKey$
          | async
          | finLocalization
            : {
                price:
                  ({ amount: String(priceInputValue$ | async), currencyCode: ('number.currency' | finLocalization) }
                  | finPrice: { stripZeroCents: true }),
              }
      "
      [primaryFooterButtonTitle]="
        'destinationSearch.filters.showResults' | finLocalization: { results: (numberOfFilteredDestinations$ | async) }
      "
      [secondaryFooterButtonTitle]="'destinationSearch.filters.clear' | finLocalization"
      (primaryFooterButtonClick)="closeFilters()"
      (secondaryFooterButtonClick)="resetPriceFilters()"
      [popoverOptionsOverride$]="popoverOptions$"
      [attr.data-testid]="'destination-search-price-filter'"
      (popoverOpeningClick)="priceFilterOpened()"
    >
      <div popover-content class="ps-medium-x flex flex--column row-gap">
        <ng-container *ngTemplateOutlet="priceFilter"></ng-container>
      </div>
    </fcom-filters>

    @if (showStopFilters) {
      <fcom-filters
        [hasFiltering]="priceInputForm.controls.stops.value === Stops.DIRECT_FLIGHTS"
        [headerText]="'destinationSearch.filters.stopsLabel' | finLocalization"
        [buttonText]="'destinationSearch.filters.stopsLabel' | finLocalization"
        [buttonTextWhenFiltered]="'destinationSearch.filters.stopsFilterDirectFlights' | finLocalization"
        [primaryFooterButtonTitle]="
          'destinationSearch.filters.showResults'
            | finLocalization: { results: (numberOfFilteredDestinations$ | async) }
        "
        [secondaryFooterButtonTitle]="'destinationSearch.filters.clear' | finLocalization"
        (primaryFooterButtonClick)="closeFilters()"
        (secondaryFooterButtonClick)="resetStops()"
        [popoverOptionsOverride$]="popoverOptions$"
        data-testid="destination-search-stops-filter"
        (popoverOpeningClick)="stopsFilterOpened()"
      >
        <div popover-content class="ps-medium-x flex flex--column row-gap">
          <ng-container *ngTemplateOutlet="stopsFilter"></ng-container>
        </div>
      </fcom-filters>
    }

    @if ((activeFilters$ | async) > 0) {
      <div>
        <fcom-button
          (clickBtn)="resetAllFilters()"
          [text]="'destinationSearch.filters.clearAllFilters' | finLocalization"
          [icon]="{ name: SvgLibraryIcon.TRASH, position: IconPosition.LEFT }"
          [theme]="ButtonTheme.LIGHT_TERTIARY_GHOST"
        >
        </fcom-button>
      </div>
    }
  </div>
}

<ng-template #priceFilter>
  <div class="flex ps-medium-b" style="margin-top: 0">
    <fcom-checkbox
      [name]="'roundTripOnly'"
      [checked]="roundTripOnly$ | async"
      (change)="roundTripOnly$.next($event)"
      data-testid="destination-search-roundtrip-filter"
      ><span class="font-body-1 medium-type">{{ 'destinationSearch.filters.roundTrip' | finLocalization }}</span>
    </fcom-checkbox>
  </div>
  <fcom-text-input
    [id]="'price-input'"
    [inputType]="InputType.NUMBER"
    [parentForm]="priceInputForm"
    [label]="
      'destinationSearch.filters.priceInput.label' | finLocalization: { currency: 'number.currency' | finLocalization }
    "
    [controlName]="'priceInput'"
    [required]="false"
    [displayMargin]="false"
    [ariaOptions]="{ describedby: 'price-input' }"
    [disableAutoComplete]="true"
    data-testid="destination-search-price-filter"
  ></fcom-text-input>
</ng-template>

<ng-template #stopsFilter>
  <div class="ps-medium-b" style="margin-top: 0">
    <fcom-radio-item-group
      [options]="[{ value: 'allFlights' }, { value: 'directFlightsOnly' }]"
      [controlName]="'stops'"
      [translationPrefix]="'destinationSearch.filters.stopsInput'"
      [titleText]="false"
      [parentForm]="priceInputForm"
      data-testid="destination-search-stops-filter"
    >
    </fcom-radio-item-group>
  </div>
</ng-template>
